import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../store';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import { SectionHeader } from '../common/SectionHeader';
import { validations } from '../../utils/validations';
import { EmployeeAutocomplete } from '../common/form/EmployeeAutocomplete';
import { EstablishmentAutocomplete } from '../common/form/EstablishmentAutocomplete';
import { WorkplaceAutocomplete } from '../common/form/WorkplaceAutocomplete';
import { InvitationWorkplaceDto } from '../../models/invitation';
import { ActivityAutocomplete } from '../common/form/ActivityAutocomplete';
import {
  establishmentLatestActions,
  getEstablishmentLatest,
  selectEstablishmentLatest,
  selectEstablishmentLatestState
} from '../../store/entities/establishmentLatest';
import { invitationListActions } from '../../store/entities/invitationList';
import { invitationTypeVisitListActions } from '../../store/entities/invitationTypeVisitList';
import { visitationListActions } from '../../store/entities/visitationList';
import { VisitationAddModalOneTimeConflict, VisitationAddOneTimeConflictData } from './VisitationAddModalOneTimeConflict';
import { useConflictResolver } from '../invitation/useConflictResolver';
import { toOneTimePersonDto } from '../invitation/oneTimePerson';
import { useNavigate } from 'react-router';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { invitationVisitService } from '@api/services/services';
import { InvitationTypeVisitRequest } from '@api/models/request/InvitationTypeVisitRequest';
import { OneTimePersonDto } from '@api/models/invitation/OneTimePersonDto';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { SpzInput } from '../common/form/SpzInput';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';
import { ShowError } from '../ShowError';
import { InvitationVisitorsSection } from '../invitation/InvitationVisitorsSection';
import { resolveVisitorConflicts } from '../invitation/resolveVisitorConflicts';

interface VisitationAddOneTimeForm {
  primaryVisitor: OneTimePersonDto;
  additionalVisitors: OneTimePersonDto[];
  visitorCompanyName?: string;
  spz?: string;
  employee?: ReferenceDto;
  establishments: ReferenceDto[];
  workplaces: InvitationWorkplaceDto[];
  activities: ReferenceDto[];
}

export const VisitationAddModalOneTime: FC = () => {
  const { t } = useTranslation();
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const establishmentLatest = useAppSelector(selectEstablishmentLatest);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const visitorConflictResolver = useConflictResolver<VisitationAddOneTimeConflictData<OneTimePersonDto>, OneTimePersonDto>();
  const [resolvedSupplierId, setResolvedSupplierId] = useState<string | undefined>(undefined);

  useEffect(() => {
    dispatch(getEstablishmentLatest());
  }, [dispatch]);

  const handleSubmit = async (data: VisitationAddOneTimeForm) => {
    const persons: OneTimePersonDto[] = [toOneTimePersonDto(data.primaryVisitor), ...data.additionalVisitors.map(toOneTimePersonDto)];

    const result = await resolveVisitorConflicts(persons, visitorConflictResolver.request);
    if (!result) return;
    const finalPersons = result.persons;
    const finalSupplierId = result.supplierId ?? resolvedSupplierId;

    const body: InvitationTypeVisitRequest = {
      persons: finalPersons,
      company: data.visitorCompanyName ? data.visitorCompanyName : undefined,
      supplierId: finalSupplierId,
      visitingPerson: data.employee!.id,
      workplaces: data.workplaces.map((x) => x.id),
      activities: data.activities.map((x) => x.id),
    };

    const res = await invitationVisitService.createInvitationTypeVisit(body);

    if (data.spz) sessionStorage.setItem(`one_time_visit_temp_spz_${res.data.id}`, data.spz);
    dispatch(visitationListActions.reload(true));
    dispatch(invitationListActions.reload(true));
    dispatch(invitationTypeVisitListActions.reload(true));
    dispatch(establishmentLatestActions.reset());

    navigate('/visitation/visit/' + res.data.id);
  };

  return (
    <>
      <VisitationAddModalOneTimeConflict data={visitorConflictResolver.conflict} onClose={visitorConflictResolver.close} />

      <SectionHeader title="visitation.oneTime" />
      <ShowResourceContent resource={resources.getLatestEstablishmentsUsedBy} fetchStateSelector={selectEstablishmentLatestState}>
        <FormikDebounce<VisitationAddOneTimeForm>
          initialValues={{
            primaryVisitor: {
              name: '',
              lastName: '',
              email: '',
              phone: '',
              language: companyCustomization!.defaultLanguage,
            },
            additionalVisitors: [],
            visitorCompanyName: '',
            spz: '',
            employee: undefined,
            establishments: establishmentLatest,
            workplaces: [],
            activities: [],
          }}
          validationSchema={Yup.object().shape({
            primaryVisitor: Yup.object()
              .shape({
                name: validations.stringRequired(t),
                lastName: validations.stringRequired(t),
                language: validations.stringRequired(t),
                email: validations.emailOptional(t),
                phone: validations.phoneOptional(t),
              })
              .required(t('errors.required')!),
            additionalVisitors: Yup.array().of(
              Yup.object().shape({
                name: validations.stringRequired(t),
                lastName: validations.stringRequired(t),
                language: validations.stringRequired(t),
                email: validations.emailOptional(t),
                phone: validations.phoneOptional(t),
              }),
            ),
            employee: validations.objectRequired(t),
            establishments: validations.arrayNotEmpty(t),
            workplaces: validations.arrayNotEmpty(t),
            activities: validations.arrayNotEmpty(t),
          })}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form>
              <FormContainer>
                <SpzInput name="spz" />
                <EmployeeAutocomplete name="employee" label={t('visitation.oneTimeEmployee')} required filterOutNonHumanRoles />
                <EstablishmentAutocomplete name="establishments" label={t('visitation.oneTimeEstablishments')} multiple required />
                <WorkplaceAutocomplete
                  name="workplaces"
                  label={t('visitation.oneTimeWorkplaces')}
                  multiple
                  required
                  estabilishments={values.establishments?.map((x) => x.id)}
                  showEstabilishmentName
                />
                <ActivityAutocomplete name="activities" label={t('visitation.oneTimeActivities')} multiple required oneTimeEntry />

                <InvitationVisitorsSection
                  showPin
                  primaryPath="primaryVisitor"
                  additionalPath="additionalVisitors"
                  companyFieldName="visitorCompanyName"
                  allowedLanguages={companyCustomization?.applicationLanguages}
                  onSupplierResolved={setResolvedSupplierId}
                />
                <ShowError resource={resources.createInvitationTypeVisit} />
                <SubmitButton />
              </FormContainer>
            </Form>
          )}
        </FormikDebounce>
      </ShowResourceContent>
    </>
  );
};
